/*
 * cart-drawer.js — AJAX mini-cart (Adjustment 2; reference CartDrawer.tsx +
 * SmartBuyButton.tsx from yalladj-new).
 *
 * Progressive enhancement over sections/cart-drawer.liquid and the header
 * cart link: without JS every add-to-cart form still POSTs natively to
 * /cart/add and the header icon links to /cart (the pre-A2 behavior). With
 * JS: add-to-cart submits go to /cart/add.js, the drawer re-renders itself
 * through Shopify's Section Rendering API (server HTML only — no client
 * money formatting, locale/currency stay correct under /ar), the header
 * badge updates live, and every product-form whose variant is in the cart
 * flips to its ✓ in-cart state. The button is a true toggle: clicking an
 * in-cart button removes that line again (quantity is locked to 1 for
 * digital goods, so the variant id addresses the line unambiguously).
 * Escape / outside-click close; focus moves to the close button on open
 * and back to the toggle on close.
 */
(function () {
  'use strict';

  var SECTION_ID = 'cart-drawer';
  var open = false;
  var busy = false;
  var toggle = null;

  function drawer() {
    return document.querySelector('[data-cart-drawer]');
  }
  function wrapper() {
    return document.getElementById('shopify-section-' + SECTION_ID);
  }

  function syncCount() {
    var root = drawer();
    var badge = document.querySelector('[data-cart-count]');
    if (!root || !badge) { return; }
    var count = parseInt(root.getAttribute('data-count') || '0', 10) || 0;
    badge.textContent = String(count);
    if (count > 0) {
      badge.removeAttribute('hidden');
    } else {
      badge.setAttribute('hidden', '');
    }
  }

  /* Flip every add-to-cart form to/from its ✓ state from the variant-id list
     the drawer section ships (single source of truth = the server cart). */
  function syncButtons() {
    var root = drawer();
    if (!root) { return; }
    var data = root.querySelector('[data-cart-variants]');
    var ids = [];
    try {
      ids = JSON.parse(data ? data.textContent : '[]');
    } catch (error) {
      ids = [];
    }
    var forms = document.querySelectorAll('form[data-product-form]');
    for (var i = 0; i < forms.length; i++) {
      var form = forms[i];
      var variantId = parseInt(form.getAttribute('data-variant-id') || '0', 10);
      var inCart = ids.indexOf(variantId) !== -1;
      form.classList.toggle('product-form--in-cart', inCart);
      var labelAdd = form.querySelector('[data-cart-label-add]');
      var labelAdded = form.querySelector('[data-cart-label-added]');
      if (labelAdd) { labelAdd.hidden = inCart; }
      if (labelAdded) { labelAdded.hidden = !inCart; }
    }
  }

  function showError(on) {
    var root = drawer();
    var error = root ? root.querySelector('[data-cart-error]') : null;
    if (error) { error.hidden = !on; }
  }

  /* Anchor the panel to the header cart icon (Sami: "stick to the cart sign
     like a dropdown"). Centered under the icon, clamped to the viewport, with
     the caret re-aimed at the icon's center. Viewport-pixel math works for
     both LTR and RTL; re-run on open, refresh and resize (inline styles live
     on the drawer root, which Section Rendering swaps out). */
  function position() {
    var root = drawer();
    if (!root || !toggle) { return; }
    var rect = toggle.getBoundingClientRect();
    var width = root.offsetWidth;
    var margin = 8;
    var center = rect.left + rect.width / 2;
    var left = Math.round(center - width / 2);
    left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));
    root.style.top = Math.round(rect.bottom + 10) + 'px';
    root.style.left = left + 'px';
    root.style.right = 'auto';
    var caret = root.querySelector('[data-cart-caret]');
    if (caret) {
      var caretX = Math.max(16, Math.min(Math.round(center - left), width - 16));
      caret.style.left = (caretX - 6) + 'px';
      caret.style.right = 'auto';
    }
  }

  function setOpen(next, focusToggle) {
    var root = drawer();
    if (!root) { return; }
    open = next;
    if (toggle) { toggle.setAttribute('aria-expanded', next ? 'true' : 'false'); }
    if (next) {
      root.removeAttribute('hidden');
      position();
      var close = root.querySelector('[data-cart-close]');
      if (close) {
        window.requestAnimationFrame(function () { close.focus(); });
      }
    } else {
      showError(false);
      root.setAttribute('hidden', '');
      if (focusToggle && toggle) { toggle.focus(); }
    }
  }

  /* Swap in the freshly server-rendered section HTML, then restore the open
     state (the rendered markup ships hidden) and re-sync badge + buttons. */
  function refresh(sectionHtml) {
    var target = wrapper();
    if (!target || typeof sectionHtml !== 'string') { return; }
    var doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    var next = doc.getElementById('shopify-section-' + SECTION_ID);
    target.innerHTML = next ? next.innerHTML : sectionHtml;
    var root = drawer();
    if (root && open) {
      root.removeAttribute('hidden');
      position();
    }
    syncCount();
    syncButtons();
  }

  function endpoint(attr, fallback) {
    var root = drawer();
    var base = (root && root.getAttribute(attr)) || fallback;
    return base + '.js';
  }

  /* Toggle-off half of the cart button: the variant is in the cart, so the
     same click removes it (Sami: the icon must add AND revoke). Quantity is
     locked to 1, so the variant id addresses exactly one line. The drawer is
     left as-is — the ✓ flipping back is the feedback. */
  function removeByVariant(form) {
    var variantId = form.getAttribute('data-variant-id');
    if (!variantId) { return; }
    var button = form.querySelector('button[name="add"]');
    busy = true;
    if (button) {
      button.disabled = true;
      button.classList.add('is-loading');
    }
    fetch(endpoint('data-change-url', '/cart/change'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 0, sections: SECTION_ID })
    })
      .then(function (response) {
        if (!response.ok) { throw new Error('change_failed'); }
        return response.json();
      })
      .then(function (data) {
        refresh(data.sections && data.sections[SECTION_ID]);
      })
      .catch(function () {
        setOpen(true);
        showError(true);
      })
      .then(function () {
        busy = false;
        if (button) {
          button.disabled = false;
          button.classList.remove('is-loading');
        }
      });
  }

  function handleAdd(form) {
    if (busy) { return; }
    if (form.classList.contains('product-form--in-cart')) {
      removeByVariant(form);
      return;
    }
    var button = form.querySelector('button[name="add"]');
    busy = true;
    if (button) {
      button.disabled = true;
      button.classList.add('is-loading');
    }
    var body = new FormData(form);
    body.append('sections', SECTION_ID);
    fetch(endpoint('data-add-url', '/cart/add'), {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: body
    })
      .then(function (response) {
        if (!response.ok) { throw new Error('add_failed'); }
        return response.json();
      })
      .then(function (data) {
        refresh(data.sections && data.sections[SECTION_ID]);
        setOpen(true);
      })
      .catch(function () {
        setOpen(true);
        showError(true);
      })
      .then(function () {
        busy = false;
        if (button) {
          button.disabled = false;
          button.classList.remove('is-loading');
        }
      });
  }

  function handleRemove(button) {
    if (busy) { return; }
    var key = button.getAttribute('data-line-key');
    if (!key) { return; }
    busy = true;
    button.disabled = true;
    fetch(endpoint('data-change-url', '/cart/change'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0, sections: SECTION_ID })
    })
      .then(function (response) {
        if (!response.ok) { throw new Error('change_failed'); }
        return response.json();
      })
      .then(function (data) {
        refresh(data.sections && data.sections[SECTION_ID]);
      })
      .catch(function () {
        button.disabled = false;
        showError(true);
      })
      .then(function () {
        busy = false;
      });
  }

  function boot() {
    if (!drawer()) { return; }
    toggle = document.querySelector('[data-cart-toggle]');

    if (toggle) {
      // Server renders an honest /cart link; only once the toggle behavior
      // exists does it become a button to assistive tech (search-overlay idiom).
      toggle.setAttribute('role', 'button');
      toggle.setAttribute('aria-haspopup', 'dialog');
      toggle.setAttribute('aria-controls', 'cart-drawer');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.addEventListener('click', function (event) {
        event.preventDefault();
        setOpen(!open);
      });
      toggle.addEventListener('keydown', function (event) {
        if (event.key === ' ' || event.key === 'Spacebar') {
          event.preventDefault();
          setOpen(!open);
        }
      });
    }

    // Delegated: survives every Section Rendering API innerHTML swap.
    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (form && form.matches && form.matches('form[data-product-form]')) {
        event.preventDefault();
        handleAdd(form);
      }
    });

    document.addEventListener('click', function (event) {
      var remove = event.target.closest ? event.target.closest('[data-cart-remove]') : null;
      if (remove) {
        handleRemove(remove);
        return;
      }
      var close = event.target.closest ? event.target.closest('[data-cart-close]') : null;
      if (close) { setOpen(false, true); }
    });

    document.addEventListener('pointerdown', function (event) {
      if (!open) { return; }
      var root = drawer();
      var insideDrawer = root && root.contains(event.target);
      var onToggle = toggle && toggle.contains(event.target);
      if (!insideDrawer && !onToggle) { setOpen(false); }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && open) { setOpen(false, true); }
    });

    window.addEventListener('resize', function () {
      if (open) { position(); }
    });

    syncCount();
    syncButtons();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}());
